'use client';

import { useEffect, useImperativeHandle, useRef } from 'react';
import type { Ref, VideoHTMLAttributes } from 'react';

/** Доля ролика в кадре, с которой он считается видимым: отсекает краешки соседних слайдов */
const VISIBLE_THRESHOLD = 0.5;

export interface LazyVideoProps extends Omit<
  VideoHTMLAttributes<HTMLVideoElement>,
  'src' | 'autoPlay' | 'preload' | 'children'
> {
  src: string;
  /** false — запускает пользователь, компонент только ставит на паузу вне экрана */
  autoPlay?: boolean;
  ref?: Ref<HTMLVideoElement>;
}

/**
 * Видео без предзагрузки: грузится и играет, пока видно на экране, вне экрана — пауза.
 * Скрытые копии (display: none, неактивные слайды за overflow) не видны и не грузятся.
 */
export const LazyVideo = ({
  src,
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  disablePictureInPicture = true,
  ref,
  ...props
}: LazyVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement, []);

  // React не выставляет muted при гидрации, а без него браузер не даст автозапуск
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let isVisible = false;
    let isPausedByUser = false;

    const onPlay = () => {
      isPausedByUser = false;
    };
    // Пауза на виду — от пользователя или конец ролика: сами не перезапускаем
    const onPause = () => {
      if (isVisible) isPausedByUser = true;
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible =
          entry.isIntersecting && entry.intersectionRatio >= VISIBLE_THRESHOLD;

        if (isVisible) {
          if (autoPlay && !isPausedByUser && video.paused) {
            video.play().catch(() => {});
          }
        } else if (!video.paused) {
          video.pause();
        }
      },
      { threshold: VISIBLE_THRESHOLD },
    );

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    observer.observe(video);

    return () => {
      observer.disconnect();
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, [autoPlay]);

  return (
    <video
      ref={videoRef}
      src={src}
      preload="none"
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      disablePictureInPicture={disablePictureInPicture}
      {...props}
    />
  );
};
